import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ProductSubscriptionRefs
 * @property {HTMLInputElement} [sellingPlanInput]
 */

/**
 * Toggles between a one-time purchase and a Recharge selling plan, keeping the
 * native `selling_plan` form field in sync with the selected radio/duration so it
 * rides the normal add-to-cart request. No Recharge API calls happen here — Recharge
 * (via Shopify Checkout Integration) registers native selling plans on the product,
 * and Shopify's own /cart/add.js and checkout do the rest.
 *
 * @extends {Component<ProductSubscriptionRefs>}
 */
class ProductSubscription extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncSellingPlan();

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  handleSubscriptionChange() {
    this.#syncSellingPlan();
  }

  #syncSellingPlan() {
    const { sellingPlanInput } = this.refs;
    if (!sellingPlanInput) return;

    const selected = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name^="subscription"]:checked')
    );
    const durationSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[name="duration"]'));
    const isSubscribing = selected?.value === 'subscribe';

    sellingPlanInput.disabled = !isSubscribing;
    sellingPlanInput.value = isSubscribing ? (durationSelect?.value ?? '') : '';
  }

  /** @param {CustomEvent} event */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const newSubscription = event.detail.data.html?.querySelector(
      `product-subscription[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newSubscription) return;

    const wasSubscribing = this.querySelector('input[name^="subscription"]:checked')?.value === 'subscribe';
    const previousDuration = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[name="duration"]'))
      ?.value;

    this.innerHTML = newSubscription.innerHTML;
    this.hidden = !newSubscription.querySelector('input[name="selling_plan"]');
    this.updatedCallback();

    if (wasSubscribing) {
      const subscribeRadio = /** @type {HTMLInputElement | null} */ (this.querySelector('input[value="subscribe"]'));
      const durationSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[name="duration"]'));

      if (subscribeRadio) {
        subscribeRadio.checked = true;

        if (durationSelect && previousDuration) {
          const hasMatchingOption = [...durationSelect.options].some((option) => option.value === previousDuration);
          if (hasMatchingOption) durationSelect.value = previousDuration;
        }
      }
    }

    this.#syncSellingPlan();
  };
}

if (!customElements.get('product-subscription')) {
  customElements.define('product-subscription', ProductSubscription);
}
